/**
 * API services for Workflow Chaining
 */

import { apiClient } from '@/lib/apiClient';
import type {
  WorkflowSubscription,
  CreateUpdateWorkflowSubscription,
  WorkflowSubscriptionListInput,
  PagedResult,
  EventCatalog,
  EventDetail,
  WorkflowDefinitionSummary,
  WorkflowDefinitionDetail,
  WorkflowChainingOverview,
  ValidationResult,
} from './types';

// apiClient's baseURL has no `/api` suffix, so these paths carry it (they match
// the ABP conventional routes: /api/core/...).
const BASE_PATH = '/api/core/workflowsubscription';
const EVENT_CATALOG_PATH = '/api/core/eventcatalog';
const COMMAND_CATALOG_PATH = '/api/core/commandcatalog';

/**
 * Workflow Subscription API
 */
export const workflowSubscriptionApi = {
  /**
   * Get a single subscription by ID
   */
  async get(id: string): Promise<WorkflowSubscription> {
    const { data } = await apiClient.get<WorkflowSubscription>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Get list of subscriptions with pagination
   */
  async getList(input: WorkflowSubscriptionListInput): Promise<PagedResult<WorkflowSubscription>> {
    const { data } = await apiClient.get<PagedResult<WorkflowSubscription>>(BASE_PATH, {
      params: input,
    });
    return data;
  },

  /**
   * Create a new subscription
   */
  async create(input: CreateUpdateWorkflowSubscription): Promise<WorkflowSubscription> {
    const { data } = await apiClient.post<WorkflowSubscription>(BASE_PATH, input);
    return data;
  },

  /**
   * Update an existing subscription
   */
  async update(id: string, input: CreateUpdateWorkflowSubscription): Promise<WorkflowSubscription> {
    const { data } = await apiClient.put<WorkflowSubscription>(`${BASE_PATH}/${id}`, input);
    return data;
  },

  /**
   * Delete a subscription
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * Get complete chaining overview for visualization
   */
  async getChainOverview(): Promise<WorkflowChainingOverview> {
    // ABP route: GetChainOverviewAsync() -> /chain-overview
    const { data } = await apiClient.get<WorkflowChainingOverview>(`${BASE_PATH}/chain-overview`);
    return data;
  },

  /**
   * Validate a subscription before saving
   */
  async validate(input: CreateUpdateWorkflowSubscription): Promise<ValidationResult> {
    const { data } = await apiClient.post<ValidationResult>(`${BASE_PATH}/validate-subscription`, input);
    return data;
  },

  /**
   * Simulate what would happen if an event was triggered
   */
  async simulateChain(eventType: string): Promise<WorkflowSubscription[]> {
    const { data } = await apiClient.get<WorkflowSubscription[]>(`${BASE_PATH}/simulate-chain`, {
      params: { eventType },
    });
    return data;
  },
};

/**
 * Event Catalog API
 */
export const eventCatalogApi = {
  /**
   * Get all events from the catalog
   */
  async getEvents(): Promise<EventCatalog[]> {
    // ABP route: GetEventsAsync() -> /events
    const { data } = await apiClient.get<EventCatalog[]>(`${EVENT_CATALOG_PATH}/events`);
    return data;
  },

  /**
   * Get details for a specific event
   */
  async getEventDetails(eventType: string): Promise<EventDetail> {
    // ABP route: GetEventDetailsAsync() -> /event-details
    const { data } = await apiClient.get<EventDetail>(`${EVENT_CATALOG_PATH}/event-details`, {
      params: { eventType },
    });
    return data;
  },
};

/**
 * Workflow Definitions Proxy API
 * Note: ABP doesn't auto-generate controller for this service, so we use chain-overview
 * which includes workflow data from the same source
 */
export const workflowDefinitionsApi = {
  /**
   * Get all workflow definitions from Elsa Server
   * Uses chain-overview endpoint which includes workflows
   */
  async getWorkflows(): Promise<WorkflowDefinitionSummary[]> {
    const { data: overview } = await apiClient.get<WorkflowChainingOverview>(`${BASE_PATH}/chain-overview`);
    // Map WorkflowNode to WorkflowDefinitionSummary format
    return (overview.workflows || []).map(w => ({
      id: w.id,
      definitionId: w.definitionId,
      name: w.name,
      description: w.description || '',
      version: w.version,
      isLatest: w.isLatest ?? false,
      isPublished: w.isPublished ?? false,
    }));
  },

  /**
   * Get details for a specific workflow
   * Not implemented - would need a dedicated endpoint
   */
  async getWorkflowDetails(definitionId: string): Promise<WorkflowDefinitionDetail> {
    // For now, get from overview and find matching workflow
    const workflows = await this.getWorkflows();
    const workflow = workflows.find(w => w.definitionId === definitionId);
    if (!workflow) {
      throw new Error(`Workflow ${definitionId} not found`);
    }
    return {
      ...workflow,
      activities: [],
      triggers: [],
    };
  },

  /**
   * Delete a workflow version (only non-published, non-latest versions)
   * Not implemented - would need a dedicated endpoint
   */
  async deleteWorkflowVersion(_id: string): Promise<void> {
    throw new Error('Delete workflow version not implemented - need dedicated API endpoint');
  },
};

/**
 * Command Catalog API
 */
export const commandCatalogApi = {
  /**
   * Get all commands from the catalog
   */
  async getCommands(): Promise<any[]> {
    // ABP route: GetCommandsAsync() -> /commands
    const { data } = await apiClient.get<any[]>(`${COMMAND_CATALOG_PATH}/commands`);
    return data;
  },
};
